/**
 * DeliverySLA constants.
 *
 * Shared, presentation-agnostic constants for the multi-seller Delivery SLA UI.
 * These are consumed by both the mapping layer (`mapDeliverySLA`) and the
 * presentation layer, so keep them free of any React / redux imports.
 */

/** UI / Amplitude priceTypeTags keys (best → priceTypeTags.best, etc.). */
export const PRICE_TYPE = {
  BEST: 'best',
  FASTEST: 'fastest'
} as const;

/**
 * scopedView API priceType values. Mapped to PRICE_TYPE for UI / Amplitude
 * `priceTypeTags` (`best` / `fastest`) on PDP + wishlist.
 */
export const SCOPED_VIEW_PRICE_TYPE = {
  BEST_PRICE: 'BEST_PRICE',
  BEST_SPEED: 'BEST_SPEED',
  /** One option that is both cheapest and fastest — nothing left to choose. */
  BEST_SPEED_PRICE: 'BEST_SPEED_PRICE'
} as const;

/**
 * True when the priceType says the option wins on price *and* speed
 * (BEST_SPEED_PRICE / BEST_PRICE_SPEED), i.e. there is no trade-off to present.
 */
export const isBestPriceAndSpeedType = (priceType?: string): boolean => {
  const key = String(priceType || '').toUpperCase();
  return key.includes('PRICE') && key.includes('SPEED');
};

/** Delivery methods returned per product detail. */
export const DELIVERY_METHOD = {
  STANDARD: 'STANDARD',
  RUSH: 'RUSH',
  PRIORITY: 'PRIORITY'
} as const;

/**
 * Default badge copy per priceType (fallback when the flag config's
 * priceTypeTags are absent). `best` -> "BEST PRICE", `fastest` -> "FASTER".
 */
export const SLA_BADGE_LABEL = {
  BEST: 'BEST PRICE',
  FASTEST: 'FASTER'
} as const;

/** Where the SLA list is being rendered. Drives small copy/badge differences. */
export const SLA_CONTEXT = {
  PDP: 'pdp',
  CART: 'cart'
} as const;

export type PriceType = (typeof PRICE_TYPE)[keyof typeof PRICE_TYPE];
export type DeliveryMethod = (typeof DELIVERY_METHOD)[keyof typeof DELIVERY_METHOD];
export type SlaContext = (typeof SLA_CONTEXT)[keyof typeof SLA_CONTEXT];

/**
 * multiSellerSlaConfig note by card count:
 * 1 → oneSlaDeliveryNote, 2+ → twoSlaDeliveryNote.
 */
export const pickSlaDeliveryNote = (
  config: {
    oneSlaDeliveryNote?: string;
    twoSlaDeliveryNote?: string;
    deliveryNote?: string;
  } = {},
  slaCardCount = 0
): string => {
  if (Number(slaCardCount) > 1) {
    return config.twoSlaDeliveryNote || '';
  }
  return config.oneSlaDeliveryNote || config.deliveryNote || '';
};

/**
 * Map scopedView / legacy priceType → UI key used with Amplitude priceTypeTags.
 * BEST_PRICE → best, BEST_SPEED → fastest (also accepts legacy best/fastest).
 */
export const normalizePriceType = (priceType?: string): PriceType => {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (
    key === SCOPED_VIEW_PRICE_TYPE.BEST_SPEED ||
    key === 'FASTEST' ||
    key === PRICE_TYPE.FASTEST.toUpperCase()
  ) {
    return PRICE_TYPE.FASTEST;
  }
  return PRICE_TYPE.BEST;
};
